import { useCallback } from 'react';
import { useConsultationFormBase } from './useConsultationFormBase';
import { usePediatricForm } from './usePediatricForm';
import { useInternalMedicineForm } from './useInternalMedicineForm';
import { useTraumatologiaForm } from './useTraumatologiaForm';
import { useDermatologiaForm } from './useDermatologiaForm';
import { useOftalmologiaForm } from './useOftalmologiaForm';
import { consultationService } from '../../../../services/consultationService';
import { consultationLabOrderService } from '../../../../services/consultationLabOrderService';
import { consultationImagingOrderService } from '../../../../services/consultationImagingOrderService';
import { consultationDiagnosisService } from '../../../../services/consultationDiagnosisService';
import { consultationPrescriptionService } from '../../../../services/consultationPrescriptionService';
import apiClient from '../../../../services/apiClient';

// Re-exports for consumers that import from 'hooks/useConsultationForm'
// (these used to live in the monolithic hook file)
export type {
  PediatricData,
  InternalMedicineData,
  TraumatologiaData,
  DermatologiaData,
  OftalmologiaData,
  DiagnosisEntry,
  PrescriptionEntry,
  LabOrderEntry,
  ImagingOrderEntry,
  VitalSignsEntry,
} from './types';

export const useConsultationForm = (editId?: number) => {
  const base = useConsultationFormBase(editId);
  const ped = usePediatricForm(base.patientBirthDate, base.patientGender);
  const im = useInternalMedicineForm(base.patientBirthDate, base.patientGender);
  const trauma = useTraumatologiaForm();
  const derma = useDermatologiaForm();
  const oftalmo = useOftalmologiaForm();

  // ── Merge: loadConsultation ──────────────────────────────────

  const loadConsultation = useCallback(async (id: number) => {
    const c = await base.fetchConsultationAndSetShared(id);
    if (!c) return;

    const birthDate = c.patient?.birth_date ?? null;
    const gender = c.patient?.gender !== undefined ? c.patient.gender : null;

    ped.loadFromConsultation(c.specialty_data, birthDate, gender);
    im.loadFromConsultation(c.specialty_data, birthDate, gender);
    trauma.loadFromConsultation(c.specialty_data);
    derma.loadFromConsultation(c.specialty_data);
    oftalmo.loadFromConsultation(c.specialty_data);

    return c;
  }, [base, ped, im, trauma, derma, oftalmo]);

  // ── Merge: submit ────────────────────────────────────────────

  const submit = useCallback(async () => {
    base.setError(null);
    const sharedPayload = base.buildSharedPayload();
    if (!sharedPayload) return;

    base.setLoading(true);
    try {
      // Merge legacy flat data + structured specialty payloads
      const payloadSpecialtyData: Record<string, any> = { ...base.getLegacySpecialtyData() };

      const pedData = ped.getPayloadData();
      if (pedData) payloadSpecialtyData.pediatric = pedData;

      const imData = im.getPayloadData();
      if (imData) payloadSpecialtyData.internal_medicine = imData;

      const traumaData = trauma.getPayloadData();
      if (traumaData) payloadSpecialtyData.traumatologia = traumaData;

      const dermaData = derma.getPayloadData();
      if (dermaData) payloadSpecialtyData.dermatologia = dermaData;

      const oftalmoData = oftalmo.getPayloadData();
      if (oftalmoData) payloadSpecialtyData.oftalmologia = oftalmoData;

      const payload: any = {
        ...sharedPayload,
        specialty_data:
          Object.keys(payloadSpecialtyData).length > 0
            ? payloadSpecialtyData
            : undefined,
      };

      let consultationId: number;
      // clinical-record-signing R6: once signed/amended, the only fields this
      // form may still touch are the root's own post-sign allowlist
      // (consultation_modality, video_call_link) — everything else (SOAP,
      // vitals, diagnoses, prescriptions, lab/imaging orders, ROS) is locked.
      // Normal navigation never reaches this form for a non-draft consultation
      // (ConsultationDetailPage hides "Editar"), but a stale tab or a direct
      // URL still could, so submit() must not attempt those writes at all
      // (must-fix item e) rather than rely on the backend rejecting them one
      // field at a time.
      const isLockedForEditing = base.isEditMode && base.consultationStatus !== null && base.consultationStatus !== 'draft';
      if (base.isEditMode && editId && isLockedForEditing) {
        await consultationService.update(editId, {
          consultation_modality: payload.consultation_modality,
        });
        base.setSuccess(true);
        setTimeout(() => base.navigate(`/consultations/${editId}`), 1500);
        return;
      }
      if (base.isEditMode && editId) {
        await consultationService.update(editId, {
          chief_complaint: payload.chief_complaint,
          present_illness: payload.present_illness,
          physical_examination: payload.physical_examination,
          general_recommendations: payload.general_recommendations,
          companion_name: payload.companion_name,
          companion_relationship: payload.companion_relationship,
          follow_up_date: payload.follow_up_date,
          requires_admission: payload.requires_admission,
          admission_reason: payload.admission_reason,
          consultation_modality: payload.consultation_modality,
          specialty_data: payload.specialty_data,
          subjective: payload.subjective,
          objective: payload.objective,
          assessment: payload.assessment,
          plan: payload.plan,
        });
        consultationId = editId;

        // The consultation update() call above deliberately does not accept
        // vital_signs/diagnoses/prescriptions (see plan doc "C1" — the create
        // flow persists them via MedicalConsultationService::createConsultation,
        // but there is no equivalent bulk-update). Reconcile them here using
        // their own dedicated CRUD endpoints (the same ones ConsultationDetailPage
        // uses for its inline editors), diffing against what was originally
        // loaded so edits, additions, and removals all persist correctly.
        const vs: Record<string, number> = {};
        Object.entries(base.vitalSigns).forEach(([key, val]) => {
          const num = parseFloat(val as string);
          if (!isNaN(num) && num > 0) vs[key] = num;
        });
        if (Object.keys(vs).length > 0) {
          if (base.vitalSignsId) {
            await apiClient.put(`/vital-signs/${base.vitalSignsId}`, vs);
          } else {
            await apiClient.post('/vital-signs', { ...vs, consultation_id: consultationId });
          }
        }

        const currentDiagnosisIds = new Set(base.diagnoses.map(d => d.id).filter(Boolean));
        for (const removedId of base.originalDiagnosisIds) {
          if (!currentDiagnosisIds.has(removedId)) {
            await consultationDiagnosisService.delete(removedId);
          }
        }
        for (const d of base.diagnoses) {
          if (d.id) {
            await consultationDiagnosisService.update(d.id, {
              diagnosis_type: d.diagnosis_type,
              observations: d.observations || undefined,
            });
          } else {
            await consultationDiagnosisService.create({
              consultation_id: consultationId,
              diagnosis_id: d.diagnosis_id,
              diagnosis_type: d.diagnosis_type,
              observations: d.observations || undefined,
            });
          }
        }

        const currentPrescriptionIds = new Set(base.prescriptions.map(p => p.id).filter(Boolean));
        for (const removedId of base.originalPrescriptionIds) {
          if (!currentPrescriptionIds.has(removedId)) {
            await consultationPrescriptionService.delete(removedId);
          }
        }
        for (const p of base.prescriptions) {
          if (!p.medication_name.trim()) continue;
          if (p.id) {
            await consultationPrescriptionService.update(p.id, {
              medication_name: p.medication_name,
              dosage: p.dosage,
              frequency: p.frequency,
              duration: p.duration,
              instructions: p.instructions || undefined,
            });
          } else {
            await consultationPrescriptionService.create({
              consultation_id: consultationId,
              medication_name: p.medication_name,
              dosage: p.dosage,
              frequency: p.frequency,
              duration: p.duration,
              instructions: p.instructions || undefined,
            });
          }
        }
      } else {
        const result = await consultationService.create(payload);
        consultationId = result.id;
      }

      // Persist Review of Systems via dedicated endpoint — neither the create
      // nor the update payload carries ROS; the backend exposes
      // PATCH /consultations/{id}/review-of-systems for both create and edit flows.
      // Failure here is logged but does not abort the flow — the consultation
      // itself is already saved and we do not want to surface a misleading error.
      base.setRosSaveWarning(false);
      if (base.reviewOfSystems && base.reviewOfSystems.length > 0) {
        try {
          await apiClient.patch(
            `/consultations/${consultationId}/review-of-systems`,
            { systems: base.reviewOfSystems },
          );
        } catch (rosErr) {
          console.error('Error saving review of systems:', rosErr);
          base.setRosSaveWarning(true);
        }
      }

      // Lab orders — entries already carrying an id were loaded from the
      // existing consultation and must be updated, not re-created (re-creating
      // duplicated every order on each re-save of an edited consultation — see
      // plan doc "C2"). In edit mode, any originally-loaded order the doctor
      // removed from the form is deleted server-side too.
      if (base.isEditMode) {
        const currentLabOrderIds = new Set(base.labOrders.map(o => o.id).filter(Boolean));
        for (const removedId of base.originalLabOrderIds) {
          if (!currentLabOrderIds.has(removedId)) {
            await consultationLabOrderService.delete(removedId);
          }
        }
      }
      for (const order of base.labOrders) {
        if (!order.test_name.trim()) continue;
        if (order.id) {
          await consultationLabOrderService.update(order.id, {
            test_name: order.test_name,
            instructions: order.instructions,
            urgency: order.urgency,
          });
          continue;
        }
        await consultationLabOrderService.create({
          consultation_id: consultationId,
          test_id: order.test_id || null,
          panel_id: order.panel_id || null,
          test_name: order.test_name,
          instructions: order.instructions,
          urgency: order.urgency,
        });
      }

      // Imaging orders — same create/skip/delete reconciliation as lab orders.
      if (base.isEditMode) {
        const currentImagingOrderIds = new Set(base.imagingOrders.map(o => o.id).filter(Boolean));
        for (const removedId of base.originalImagingOrderIds) {
          if (!currentImagingOrderIds.has(removedId)) {
            await consultationImagingOrderService.delete(removedId);
          }
        }
      }
      for (const order of base.imagingOrders) {
        if (!order.imaging_study_id) continue;
        if (order.id) {
          await consultationImagingOrderService.update(order.id, {
            imaging_study_id: order.imaging_study_id,
            clinical_indication: order.clinical_indication,
            priority: order.priority,
          });
          continue;
        }
        await consultationImagingOrderService.create({
          consultation_id: consultationId,
          imaging_study_id: order.imaging_study_id,
          clinical_indication: order.clinical_indication,
          priority: order.priority,
        });
      }

      base.setSuccess(true);
      setTimeout(
        () =>
          base.navigate(
            base.isEditMode && editId
              ? `/consultations/${editId}`
              : '/consultations',
          ),
        1500,
      );
    } catch (err: any) {
      console.error(
        `Error ${base.isEditMode ? 'updating' : 'creating'} consultation:`,
        err,
      );
      base.setError(
        err.response?.data?.message ||
          `Error al ${base.isEditMode ? 'actualizar' : 'crear'} la consulta`,
      );
    } finally {
      base.setLoading(false);
    }
  }, [base, ped, im, trauma, derma, oftalmo, editId]);

  // ── Public API (matches original useConsultationForm exactly) ──

  return {
    loading: base.loading,
    loadingEdit: base.loadingEdit,
    loadingDoctors: base.loadingDoctors,
    loadingPreAuths: base.loadingPreAuths,
    error: base.error,
    success: base.success,
    rosSaveWarning: base.rosSaveWarning,
    isEditMode: base.isEditMode,
    hasUnsavedChanges: base.hasUnsavedChanges,
    lastSavedAt: base.lastSavedAt,
    draftRecoveryPrompt: base.draftRecoveryPrompt,
    confirmDraftRecovery: base.confirmDraftRecovery,
    dismissDraftRecovery: base.dismissDraftRecovery,
    loadConsultation,
    submit,
    // Form fields
    patientId: base.patientId,
    setPatientId: base.setPatientId,
    doctorId: base.doctorId,
    setDoctorId: base.setDoctorId,
    appointmentId: base.appointmentId,
    setAppointmentId: base.setAppointmentId,
    preAuthorizationId: base.preAuthorizationId,
    setPreAuthorizationId: base.setPreAuthorizationId,
    activePreAuths: base.activePreAuths,
    loadActivePreAuths: base.loadActivePreAuths,
    chiefComplaint: base.chiefComplaint,
    setChiefComplaint: base.setChiefComplaint,
    currentIllness: base.currentIllness,
    setCurrentIllness: base.setCurrentIllness,
    physicalExamination: base.physicalExamination,
    setPhysicalExamination: base.setPhysicalExamination,
    generalRecommendations: base.generalRecommendations,
    setGeneralRecommendations: base.setGeneralRecommendations,
    companionName: base.companionName,
    setCompanionName: base.setCompanionName,
    companionRelationship: base.companionRelationship,
    setCompanionRelationship: base.setCompanionRelationship,
    followUpDate: base.followUpDate,
    setFollowUpDate: base.setFollowUpDate,
    requiresAdmission: base.requiresAdmission,
    setRequiresAdmission: base.setRequiresAdmission,
    admissionReason: base.admissionReason,
    setAdmissionReason: base.setAdmissionReason,
    consultationModality: base.consultationModality,
    setConsultationModality: base.setConsultationModality,
    videoCallLink: base.videoCallLink,
    specialtyData: base.specialtyData,
    setSpecialtyData: base.setSpecialtyData,
    // Specialty data (structured)
    pediatricData: ped.pediatricData,
    setPediatricData: ped.setPediatricData,
    setPediatricField: ped.setPediatricField,
    internalMedicineData: im.internalMedicineData,
    setInternalMedicineData: im.setInternalMedicineData,
    setInternalMedicineField: im.setInternalMedicineField,
    traumatologiaData: trauma.traumatologiaData,
    setTraumatologiaData: trauma.setTraumatologiaData,
    setTraumatologiaField: trauma.setTraumatologiaField,
    dermatologiaData: derma.dermatologiaData,
    setDermatologiaData: derma.setDermatologiaData,
    setDermatologiaField: derma.setDermatologiaField,
    oftalmologiaData: oftalmo.oftalmologiaData,
    setOftalmologiaData: oftalmo.setOftalmologiaData,
    setOftalmologiaField: oftalmo.setOftalmologiaField,
    // Patient info
    patientBirthDate: base.patientBirthDate,
    setPatientBirthDate: base.setPatientBirthDate,
    patientGender: base.patientGender,
    setPatientGender: base.setPatientGender,
    doctorSpecialty: base.doctorSpecialty,
    currentDoctor: base.currentDoctor,
    // SOAP fields
    soapFields: base.soapFields,
    setSoapFields: base.setSoapFields,
    // Review of Systems
    reviewOfSystems: base.reviewOfSystems,
    setReviewOfSystems: base.setReviewOfSystems,
    // Vital signs
    vitalSigns: base.vitalSigns,
    updateVitalSign: base.updateVitalSign,
    // Diagnoses
    diagnoses: base.diagnoses,
    addDiagnosis: base.addDiagnosis,
    removeDiagnosis: base.removeDiagnosis,
    updateDiagnosisObservation: base.updateDiagnosisObservation,
    diagnosisResults: base.diagnosisResults,
    searchDiagnoses: base.searchDiagnoses,
    searchingDiagnoses: base.searchingDiagnoses,
    // Prescriptions
    prescriptions: base.prescriptions,
    addPrescription: base.addPrescription,
    removePrescription: base.removePrescription,
    updatePrescription: base.updatePrescription,
    // Lab and Imaging orders
    labOrders: base.labOrders,
    setLabOrders: base.setLabOrders,
    imagingOrders: base.imagingOrders,
    setImagingOrders: base.setImagingOrders,
    // Search
    patients: base.patients,
    searchPatients: base.searchPatients,
    searchingPatients: base.searchingPatients,
    loadedPatientLabel: base.loadedPatientLabel,
    clearLoadedPatientLabel: base.clearLoadedPatientLabel,
    setPatients: base.setPatients,
    doctors: base.doctors,
    fetchDoctors: base.fetchDoctors,
    // clinical-delegation / clinical-record-signing
    myDoctors: base.myDoctors,
    isAssistant: base.isAssistant,
    loadedDoctor: base.loadedDoctor,
    consultationStatus: base.consultationStatus,
    consultationLockVersion: base.consultationLockVersion,
    consultationCreatedBy: base.consultationCreatedBy,
  };
};

export type UseConsultationFormReturn = ReturnType<typeof useConsultationForm>;
